/**
 * Pushing finished and in-progress games to the server.
 *
 * The scorekeeper is offline-first, so syncing is best-effort and never blocks
 * the table: whole-game snapshots are PUT to an idempotent endpoint, so a failed
 * attempt just stays in the queue and is retried later. Nothing needs to track
 * which parts arrived.
 */

import { useEffect } from 'react';
import type { ScoreGame } from '@shared/types/game';
import type { KlaverjasScoreGame } from '@shared/types/klaverjas';
import { useScorekeeper } from '@/store/scorekeeper';
import { useKlaverjasScore } from '@/store/klaverjasScore';
import { useAuth } from '@/store/auth';

/**
 * Statuses that mean "not now" rather than "never".
 *
 * This distinction is load-bearing. Dropping a game from the queue deletes the
 * only copy of it — the server has never seen it. A 401 is not a broken payload;
 * it is a device that needs to be let in again, and treating it as permanent
 * would silently wipe every unsynced game on the phone the moment authentication
 * is switched on, or a grant expires, or someone is revoked.
 */
const RETRYABLE = new Set([401, 403, 408, 409, 425, 429]);

/**
 * The one 403 that will never become a 200.
 *
 * A 403 is normally retryable here, for the reason above. But a potje owned by
 * another device is refused *for good*, so retrying it means an item that
 * circles the queue every minute forever. Stop retrying — and keep the game on
 * this phone, which is a separate decision and stays untouched.
 */
const PERMANENT_CODE = 'NOT_MY_GAME';

async function pushGame(game: ScoreGame | KlaverjasScoreGame): Promise<boolean> {
  try {
    const response = await fetch(`/api/games/${game.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(game),
    });

    if (response.ok) return true;

    /*
     * Tell the auth store, but keep the game. These two are separate decisions
     * and conflating them is the bug this whole file is shaped around: the app
     * should lock, and the unsynced scores should still be sitting in the queue
     * when the device is let back in.
     */
    if (response.status === 401) useAuth.getState().onUnauthorized();

    if (response.status === 403) {
      // Read the body rather than assuming: any other 403 stays retryable.
      const code = await response
        .json()
        .then((body: { code?: string }) => body?.code)
        .catch(() => undefined);
      if (code === PERMANENT_CODE) {
        console.warn(`sync gave up on game ${game.id}: van een ander toestel`);
        return true;
      }
    }

    if (RETRYABLE.has(response.status) || response.status >= 500) {
      console.warn(`sync deferred for game ${game.id}: ${response.status}`);
      return false;
    }

    // A genuine 4xx — a malformed or rejected document. Retrying cannot help,
    // so stop, but say so loudly rather than dropping it in silence.
    console.error(`sync rejected game ${game.id} permanently: ${response.status}`);
    return true;
  } catch {
    return false;
  }
}

let running = false;

export async function flushSyncQueue(): Promise<void> {
  if (running || !navigator.onLine) return;
  running = true;

  try {
    /*
     * Two stores, one queue to drain. Klaverjassen keeps its own store rather
     * than widening the scorekeeper's — see the note at the top of
     * `store/klaverjasScore.ts` — so this is the one place that has to know
     * about both.
     */
    const sources = [
      useScorekeeper.getState() as {
        games: Record<string, ScoreGame | KlaverjasScoreGame>;
        pendingSync: string[];
        markSynced: (id: string) => void;
      },
      useKlaverjasScore.getState(),
    ];

    for (const { games, pendingSync, markSynced } of sources) {
      for (const gameId of [...pendingSync]) {
        const game = games[gameId];
        if (!game) {
          markSynced(gameId);
          continue;
        }
        if (await pushGame(game)) markSynced(gameId);
      }
    }
  } finally {
    running = false;
  }
}

/**
 * Flush on start-up, whenever the connection comes back, and when the app is
 * brought to the foreground — the three moments a phone at a campsite actually
 * regains network.
 */
export function useBackgroundSync(): void {
  useEffect(() => {
    void flushSyncQueue();

    const onOnline = () => void flushSyncQueue();
    const onVisible = () => {
      if (document.visibilityState === 'visible') void flushSyncQueue();
    };

    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);

    // Catch games finished while the app stayed open and online.
    const timer = window.setInterval(() => void flushSyncQueue(), 60_000);

    return () => {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, []);
}
